// THE KEYS THAT OPEN A BRANCH DOWN TO A CLICKED ELEMENT.
//
// Owner, 2026-10-19: "project files is not expanding and selecting correct
// item in tree when clicking item in 3D view in the model viewer / model on
// project cards".
//
// react-simple-tree-menu identifies a node by its ancestors' ids joined with
// "/", so opening a branch means handing it EVERY prefix of the path, not just
// the leaf. Viewer3d built exactly four of them by hand - path[0] through
// path[3] - which matched the project-files tree (root > category > group >
// component) and nothing else: a shallower path threw on `path[3].id` and
// expanded nothing, a deeper one left the element's own branch shut.
//
// Pulled out of the component so it can be tested without mounting a viewer,
// which is why the four-level assumption survived as long as it did.

/**
 * ["a","a/b","a/b/c"] for a path of three nodes.
 * An empty or missing path yields [] rather than throwing - the caller then
 * has nothing to open, which is a real case (an element the tree does not
 * carry) and not an error worth breaking the click over.
 */
export function expandKeysForPath(path) {
  if (!Array.isArray(path) || !path.length) return [];
  const ids = path.map((n) => (n && n.id !== undefined && n.id !== null ? String(n.id) : null));
  // A gap in the middle makes every key past it wrong, so stop at the gap and
  // open as far as we can rather than emitting "a/undefined/c".
  const upto = ids.indexOf(null) === -1 ? ids.length : ids.indexOf(null);
  return ids.slice(0, upto).map((_, i) => ids.slice(0, i + 1).join("/"));
}

/** The node to scroll to: the deepest one the path actually names. */
export function scrollTargetId(path) {
  if (!Array.isArray(path) || !path.length) return null;
  const last = path[path.length - 1];
  return last && last.id !== undefined && last.id !== null ? last.id : null;
}

/**
 * The mesh a 3D click refers to, looked up across the loaded files.
 *
 * Owner, 2026-10-20: the Project Files tree did not expand or select on a 3D
 * click. The lookup itself was right; it was reading `let filesMeshes = []`
 * declared in the component body, which React resets on EVERY render, so by
 * the time anyone clicked it searched an empty list. This takes the list as an
 * argument precisely so that mistake cannot hide inside a component again.
 *
 * Returns one entry per file - the matching mesh, or undefined - because the
 * caller distinguishes "which file held it" from "was it found at all".
 * `found` is the flag worth testing: an array of undefineds is truthy, and
 * that is what made the old `if (clickedFilesMesh)` fire on every click.
 */
export function findClickedMesh(loadedFiles, uniqueId) {
  const files = Array.isArray(loadedFiles) ? loadedFiles : [];
  const perFile = files.map((file) =>
    (Array.isArray(file) ? file.find((m) => m && m.UniqueId === uniqueId) : undefined)
  );
  const hit = perFile.find(Boolean) || null;
  return { perFile, found: !!hit, mesh: hit };
}
